<template>
  <v-list class="mt-0 pt-0 primary-navigation">
    <v-list-item
      v-for="item in localNavItems"
      :key="item.to"
      :to="item.to ? item.to : undefined"
      :href="item.href ? item.href : undefined"
      :target="item.target"
      :rel="item.rel"
      class="nav-item"
      active-class="grey-lighten-3 font-weight-bold"
      :active="item.to ? isRouteActive(item.to) : false"
      color="primary"
      @click="handleItemClick(item)"
    >
      <template #prepend>
        <v-badge
          v-if="item.badge && item.badge.condition && item.badge.condition()"
          bordered
          location="bottom"
          :color="item.badge.color"
          dot
          offset-x="10"
          offset-y="10"
        >
          <v-icon>{{ item.icon }}</v-icon>
        </v-badge>
        <v-badge
          v-else-if="isNewItem(item) && showIsNew"
          content="NEW"
          color="orange"
          offset-x="10"
          offset-y="10"
        >
          <v-icon>{{ item.icon }}</v-icon>
        </v-badge>
        <v-icon v-else>
          {{ item.icon }}
        </v-icon>
      </template>

      <v-list-item>
        <v-list-item-title>
          {{ $t(item.label) }}
          <v-badge
            v-if="isBetaItem(item)"
            content="BETA"
            color="orange"
            class="ml-2"
          />
        </v-list-item-title>
      </v-list-item>
    </v-list-item>
  </v-list>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

// @ts-ignore: Declaration file warning can be ignored
import brandingMixin from '@/mixins/branding'
import { NavItem } from '@/modules/navigation/models/Navitem'

// Extended interface for NavItem
interface ExtendedNavItem extends NavItem {
  isNew?: boolean
  isBeta?: boolean
}

const props = defineProps<{
  navItems: ExtendedNavItem[]
  showRegeneration?: boolean
}>()

const route = useRoute()
const { isPlatformMarkero } = brandingMixin.computed

const mapNavItems = (): ExtendedNavItem[] => {
  return props.navItems.map((item: ExtendedNavItem) => {
    return {
      ...item,
      to: route?.params?.id ? `${item.to?.replace(/\/$/, '')}/${route.params.id}` : item.to,
    }
  })
}

const localNavItems = ref<ExtendedNavItem[]>(mapNavItems())

watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    if (newPath !== oldPath) {
      localNavItems.value = mapNavItems()
    }
  }
)

const emit = defineEmits<{
  (e: 'navigation-click', value: string): void
}>()

const showIsNew = computed(() => {
  return !isPlatformMarkero
})

// Type guard functions
function isNewItem(item: NavItem): item is ExtendedNavItem & { isNew: boolean } {
  return 'isNew' in item && Boolean(item['isNew'])
}

function isBetaItem(item: NavItem): item is ExtendedNavItem & { isBeta: boolean } {
  return 'isBeta' in item && Boolean(item['isBeta'])
}

const isRouteActive = (itemPath: string) => {
  if (!itemPath) return false

  const currentPath = route.path
  const normalizedItemPath = itemPath.replace(/\/$/, '')
  const normalizedCurrentPath = currentPath.replace(/\/$/, '')
  return (
    normalizedCurrentPath === normalizedItemPath ||
    normalizedCurrentPath.startsWith(normalizedItemPath + '/')
  )
}

const handleItemClick = (item: NavItem) => {
  emit('navigation-click', item.trackingLabel)
}
</script>

<style scoped>
.primary-navigation {
  flex: 1;
  overflow-y: auto !important;
  overflow-x: hidden;
}

.nav-item {
  padding-left: 16px !important;
}
</style>
